import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lock, ArrowRight, Loader2 } from "lucide-react";
import { formatApiError } from "@/lib/apiError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage({ onLogin }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setError("Both passwords must match.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(data.detail, "That reset link is no longer valid."));
      onLogin(data.user);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 py-16"
      style={{ backgroundColor: "var(--mp-paper)" }}
      data-testid="reset-password-page"
    >
      <div className="w-full max-w-md">
        <div
          className="border rounded-sm p-7"
          style={{ borderColor: "var(--mp-line)", backgroundColor: "var(--mp-card)" }}
        >
          <h1
            className="text-2xl mb-1"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
          >
            Choose a new password
          </h1>

          {!token ? (
            <p
              className="text-sm mt-3"
              style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
              data-testid="reset-missing-token"
            >
              This link is incomplete. Please request a new reset link from the{" "}
              <Link to="/login" className="underline underline-offset-2">
                sign-in page
              </Link>
              .
            </p>
          ) : (
            <form onSubmit={submit} className="space-y-3 mt-5">
              <div
                className="flex items-center gap-2 px-3 py-2.5 rounded-sm border"
                style={{ borderColor: "var(--mp-line)", backgroundColor: "var(--mp-surface)" }}
              >
                <Lock className="w-4 h-4 shrink-0" style={{ color: "var(--mp-muted)" }} />
                <input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type="password"
                  required
                  autoComplete="new-password"
                  placeholder="New password (at least 8 characters)"
                  data-testid="reset-password-input"
                  className="w-full min-w-0 text-sm bg-transparent outline-none"
                  style={{ color: "var(--mp-text)", fontFamily: "'Manrope', sans-serif" }}
                />
              </div>
              <div
                className="flex items-center gap-2 px-3 py-2.5 rounded-sm border"
                style={{ borderColor: "var(--mp-line)", backgroundColor: "var(--mp-surface)" }}
              >
                <Lock className="w-4 h-4 shrink-0" style={{ color: "var(--mp-muted)" }} />
                <input
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  type="password"
                  required
                  autoComplete="new-password"
                  placeholder="Repeat the new password"
                  data-testid="reset-confirm-input"
                  className="w-full min-w-0 text-sm bg-transparent outline-none"
                  style={{ color: "var(--mp-text)", fontFamily: "'Manrope', sans-serif" }}
                />
              </div>

              {error && (
                <p
                  className="text-xs"
                  style={{ color: "#C2410C", fontFamily: "'Manrope', sans-serif" }}
                  data-testid="reset-error"
                >
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="reset-submit-btn"
                className="flex items-center justify-center gap-2 w-full text-sm font-medium py-3.5 rounded-sm transition-opacity hover:opacity-90 disabled:opacity-60"
                style={{
                  backgroundColor: "var(--mp-copper-solid)",
                  color: "#FFFFFF",
                  fontFamily: "'Manrope', sans-serif",
                }}
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                Save new password
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
